import {
  ChevronsUp,
  ChevronUp,
  Equal,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react'
import { DEFAULT_STATUS_KEY, TASK_PRIORITIES, TASK_TYPES, resolveStatus } from '@jira-clone/shared-types'
import type { Issue } from './api'

const CHIP_PALETTE = [
  'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300',
  'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300',
  'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300',
  'bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300',
  'bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300',
  'bg-pink-100 text-pink-700 dark:bg-pink-950/60 dark:text-pink-300',
  'bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300',
] as const

function hashString(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0
  }
  return hash
}

export function chipToneFor(value: string): string {
  return CHIP_PALETTE[hashString(value) % CHIP_PALETTE.length]
}

export interface EpicRef {
  id: string
  key: string
  title: string
}

export function buildEpicLookup(issues: Issue[]): Map<string, EpicRef> {
  const map = new Map<string, EpicRef>()
  for (const issue of issues) {
    if (issue.type === 'epic') {
      map.set(issue.id, { id: issue.id, key: issue.key, title: issue.title })
    }
  }
  return map
}

export interface SubtaskStats {
  done: number
  total: number
}

export function buildSubtaskStats(issues: Issue[]): Map<string, SubtaskStats> {
  const map = new Map<string, SubtaskStats>()
  for (const issue of issues) {
    if (!issue.parent_id) continue
    const stats = map.get(issue.parent_id) ?? { done: 0, total: 0 }
    stats.total += 1
    if (issue.status_category === 'done') stats.done += 1
    map.set(issue.parent_id, stats)
  }
  return map
}

export type DueUrgency = 'overdue' | 'soon' | 'normal'

export function dueUrgency(dueDate: string | null | undefined, isDone: boolean): DueUrgency | null {
  if (!dueDate || isDone) return null
  const due = new Date(dueDate).getTime()
  if (isNaN(due)) return null
  const now = Date.now()
  const oneDay = 24 * 60 * 60 * 1000
  if (due < now) return 'overdue'
  if (due - now <= 2 * oneDay) return 'soon'
  return 'normal'
}

export function formatDueDate(dueDate: string): string {
  const d = new Date(dueDate)
  if (isNaN(d.getTime())) return dueDate
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export function sortByBacklogOrder<T extends { backlog_order?: number; position?: number }>(issues: T[]): T[] {
  return [...issues].sort((a, b) => {
    const orderA = a.backlog_order ?? a.position ?? 0
    const orderB = b.backlog_order ?? b.position ?? 0
    return orderA - orderB
  })
}

export function sumStoryPoints(issues: Issue[]): number {
  return issues.reduce((sum, issue) => sum + (issue.story_points ?? issue.estimatedHours ?? 0), 0)
}

export function orderBetween(before: number | undefined, after: number | undefined): number {
  if (before === undefined && after === undefined) return 1000
  if (before === undefined) return (after as number) - 1000
  if (after === undefined) return before + 1000
  return (before + after) / 2
}

export const PRIORITY_LABEL: Record<string, string> = Object.fromEntries(TASK_PRIORITIES.map((p) => [p.key, p.label]))

export const PRIORITY_TONE: Record<string, string> = {
  critical: 'text-red-600 dark:text-red-400',
  high: 'text-orange-600 dark:text-orange-400',
  medium: 'text-amber-600 dark:text-amber-400',
  low: 'text-blue-600 dark:text-blue-400',
}

export const PRIORITY_ICON: Record<string, LucideIcon> = {
  critical: ChevronsUp,
  high: ChevronUp,
  medium: Equal,
  low: ChevronDown,
}

/** Priority choices for pickers and filters, most urgent first. */
export const PRIORITY_OPTIONS = [...TASK_PRIORITIES].reverse().map((p) => ({ value: p.key, label: p.label }))

/** Issue type choices for pickers and filters. */
export const ISSUE_TYPE_OPTIONS = TASK_TYPES.map((t) => ({ value: t.key, label: t.name }))

export const TYPE_LABEL: Record<string, string> = Object.fromEntries(TASK_TYPES.map((t) => [t.key, t.name]))

export const TYPE_BADGE_TONE: Record<string, string> = {
  epic: 'bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300',
  story: 'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-300',
  task: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
  bug: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300',
  subtask: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
}

/**
 * Whether an issue belongs in a status column. Statuses are the fixed task
 * configuration, and an issue's `status` is always one of its keys, so this is
 * a direct comparison (an older name or alias is still understood).
 */
export function doesIssueMatchStatus(
  issue: { status?: string | null; status_category?: string | null } | null | undefined,
  status: { id: string; name: string; category?: string } | null | undefined,
  _allStatuses?: Array<{ id: string; name: string; category?: string }>
): boolean {
  if (!issue || !status) return false
  const issueStatus = (issue.status ?? '').trim()
  if (!issueStatus) return status.id === DEFAULT_STATUS_KEY
  return (resolveStatus(issueStatus)?.key ?? issueStatus) === status.id
}
